import { B } from "../shared/world";

// Every sound is synthesised with WebAudio: no Mojang audio, nothing to download.
// Each one is a few lines of filtered noise and tones, tuned to read as the
// Minecraft sound it stands in for.

type Pos = { x: number; y: number; z: number };

export type SoundName =
  | "hurt" | "crit" | "death" | "step" | "land" | "splash" | "chestOpen" | "eat" | "pop"
  | "sign" | "signBreak" | "chat" | "click" | "tick" | "start" | "win" | "refill"
  | "draw" | "bow" | "ding" | "arrowHit" | "cast" | "reel";

export type Surface = "grass" | "sand" | "stone" | "wood";

const HEAR_DIST = 24;

export class Sound {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  enabled = true;
  private listener = { x: 0, y: 0, z: 0, yaw: 0 };

  /** Browsers only allow audio after a tap or keypress. Call from any gesture. */
  unlock() {
    if (!this.ctx) {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.enabled ? 0.6 : 0;
      this.master.connect(this.ctx.destination);
      const len = this.ctx.sampleRate;
      this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const d = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
  }

  setEnabled(on: boolean) {
    this.enabled = on;
    if (this.master) this.master.gain.value = on ? 0.6 : 0;
  }

  setListener(x: number, y: number, z: number, yaw: number) {
    Object.assign(this.listener, { x, y, z, yaw });
  }

  /** Plays a sound, from a point in the world if `at` is given (quieter with distance, panned left/right). */
  play(name: SoundName, at?: Pos, opts: { vol?: number; surface?: Surface } = {}) {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.enabled || ctx.state !== "running") return;
    let vol = opts.vol ?? 1;
    let out: AudioNode = this.master;
    if (at) {
      const l = this.listener;
      const dx = at.x - l.x, dy = at.y - l.y, dz = at.z - l.z;
      const d = Math.hypot(dx, dy, dz);
      if (d > HEAR_DIST) return;
      vol *= Math.pow(1 - d / HEAR_DIST, 1.6);
      if (ctx.createStereoPanner && d > 0.5) {
        const p = ctx.createStereoPanner();
        // Camera right is (cos yaw, -sin yaw) on x/z.
        p.pan.value = Math.max(-0.85, Math.min(0.85, (dx * Math.cos(l.yaw) - dz * Math.sin(l.yaw)) / d));
        p.connect(this.master);
        out = p;
      }
    }
    if (vol < 0.01) return;
    const t = ctx.currentTime;
    const r = (a: number, b: number) => a + Math.random() * (b - a);
    switch (name) {
      case "hurt":
      case "death": {
        // The "oof": a short falling buzz with a thump under it.
        const low = name === "death" ? 0.75 : 1;
        const p = r(0.9, 1.1) * low;
        this.tone(out, t, { type: "sawtooth", f0: 330 * p, f1: 160 * p, dur: name === "death" ? 0.4 : 0.2, gain: 0.32 * vol, lp: 1300 });
        this.tone(out, t, { type: "square", f0: 165 * p, f1: 90 * p, dur: 0.16, gain: 0.12 * vol, lp: 700 });
        this.noise(out, t, { dur: 0.08, gain: 0.35 * vol, type: "lowpass", f0: 600 });
        break;
      }
      case "crit":
        this.play("hurt", at, { vol: opts.vol });
        // The bright metallic snap on top.
        this.noise(out, t, { dur: 0.12, gain: 0.45 * vol, type: "bandpass", f0: 4200, f1: 2600, q: 3 });
        this.tone(out, t, { type: "triangle", f0: 1900, f1: 1300, dur: 0.1, gain: 0.12 * vol });
        break;
      case "step": {
        const s = opts.surface ?? "grass";
        if (s === "grass") this.noise(out, t, { dur: 0.11, gain: 0.22 * vol, type: "lowpass", f0: r(900, 1300), f1: 300 });
        else if (s === "sand") this.noise(out, t, { dur: 0.13, gain: 0.16 * vol, type: "bandpass", f0: r(2200, 3000), q: 0.8 });
        else if (s === "stone") {
          this.noise(out, t, { dur: 0.06, gain: 0.3 * vol, type: "bandpass", f0: r(1500, 2100), q: 2.5 });
          this.tone(out, t, { type: "triangle", f0: r(150, 190), f1: 100, dur: 0.06, gain: 0.12 * vol });
        } else {
          this.tone(out, t, { type: "triangle", f0: r(190, 240), f1: 140, dur: 0.07, gain: 0.25 * vol });
          this.noise(out, t, { dur: 0.05, gain: 0.12 * vol, type: "bandpass", f0: 900, q: 2 });
        }
        break;
      }
      case "land":
        this.noise(out, t, { dur: 0.18, gain: 0.4 * vol, type: "lowpass", f0: 500, f1: 120 });
        break;
      case "splash":
        this.noise(out, t, { dur: 0.6, gain: 0.35 * vol, type: "lowpass", f0: 3500, f1: 400, attack: 0.02 });
        this.noise(out, t + 0.05, { dur: 0.35, gain: 0.15 * vol, type: "bandpass", f0: 1800, q: 1.5 });
        break;
      case "chestOpen":
        // Creaky hinge, then the lid knocking back.
        this.tone(out, t, { type: "sawtooth", f0: r(85, 100), f1: 150, dur: 0.32, gain: 0.18 * vol, lp: 900, wobble: 18 });
        this.noise(out, t + 0.28, { dur: 0.08, gain: 0.3 * vol, type: "bandpass", f0: 700, q: 1.5 });
        this.tone(out, t + 0.28, { type: "triangle", f0: 160, f1: 110, dur: 0.09, gain: 0.2 * vol });
        break;
      case "eat":
        for (let i = 0; i < 3; i++) this.noise(out, t + i * 0.13, { dur: 0.08, gain: 0.3 * vol, type: "bandpass", f0: r(1200, 2200), q: 1.2 });
        break;
      case "pop":
        // Item pickup.
        this.tone(out, t, { type: "sine", f0: r(500, 700), f1: r(1100, 1500), dur: 0.08, gain: 0.25 * vol });
        break;
      case "sign":
        this.tone(out, t, { type: "triangle", f0: 310, f1: 210, dur: 0.09, gain: 0.3 * vol });
        this.noise(out, t, { dur: 0.06, gain: 0.18 * vol, type: "bandpass", f0: 1100, q: 2 });
        break;
      case "signBreak":
        for (let i = 0; i < 4; i++) this.noise(out, t + i * 0.035, { dur: 0.06, gain: 0.25 * vol, type: "bandpass", f0: r(600, 1400), q: 2 });
        this.tone(out, t, { type: "triangle", f0: 220, f1: 120, dur: 0.12, gain: 0.2 * vol });
        break;
      case "chat":
        // A soft note-block pling so you notice a sign went up somewhere.
        this.tone(out, t, { type: "sine", f0: 1175, dur: 0.25, gain: 0.08 * vol });
        this.tone(out, t, { type: "sine", f0: 2350, dur: 0.12, gain: 0.03 * vol });
        break;
      case "click":
        this.tone(out, t, { type: "square", f0: 1400, f1: 900, dur: 0.025, gain: 0.12 * vol, lp: 3000 });
        this.noise(out, t, { dur: 0.02, gain: 0.15 * vol, type: "highpass", f0: 2000 });
        break;
      case "tick":
        this.tone(out, t, { type: "sine", f0: 880, dur: 0.12, gain: 0.25 * vol });
        break;
      case "start":
        [784, 1047].forEach((f, i) => this.tone(out, t + i * 0.12, { type: "sine", f0: f, dur: 0.35, gain: 0.25 * vol }));
        break;
      case "win":
        // Level-up arpeggio.
        [523, 659, 784, 1047, 1319].forEach((f, i) => this.tone(out, t + i * 0.09, { type: "triangle", f0: f, dur: 0.3, gain: 0.2 * vol }));
        break;
      case "draw":
        // The string creaking back: a slow rising, wobbling groan.
        this.tone(out, t, { type: "sawtooth", f0: 70, f1: 130, dur: 0.7, gain: 0.07 * vol, lp: 700, wobble: 22 });
        this.noise(out, t, { dur: 0.5, gain: 0.06 * vol, type: "bandpass", f0: 900, f1: 1500, q: 3, attack: 0.15 });
        break;
      case "bow":
        // Release: the string's twang and the whoosh of the arrow leaving.
        this.tone(out, t, { type: "triangle", f0: r(210, 240), f1: 95, dur: 0.22, gain: 0.3 * vol });
        this.tone(out, t, { type: "square", f0: r(420, 480), f1: 190, dur: 0.08, gain: 0.05 * vol, lp: 1800 });
        this.noise(out, t, { dur: 0.25, gain: 0.22 * vol, type: "bandpass", f0: 2600, f1: 700, q: 1.2 });
        break;
      case "ding":
        // Your arrow found someone: the bright two-note pling.
        this.tone(out, t, { type: "sine", f0: 1568, dur: 0.18, gain: 0.2 * vol });
        this.tone(out, t + 0.07, { type: "sine", f0: 2093, dur: 0.35, gain: 0.18 * vol });
        break;
      case "arrowHit":
        // Thunk into a block.
        this.noise(out, t, { dur: 0.07, gain: 0.3 * vol, type: "bandpass", f0: r(500, 800), q: 2 });
        this.tone(out, t, { type: "triangle", f0: 140, f1: 80, dur: 0.08, gain: 0.25 * vol });
        break;
      case "cast":
        // Rod cast: a swish, high to low.
        this.noise(out, t, { dur: 0.32, gain: 0.3 * vol, type: "bandpass", f0: 3200, f1: 600, q: 1.4, attack: 0.03 });
        break;
      case "reel":
        // Reeling in: the ratchet ticking over, then the bobber's little plop home.
        for (let i = 0; i < 7; i++) this.noise(out, t + i * 0.035, { dur: 0.02, gain: 0.2 * vol, type: "highpass", f0: 2500 });
        this.tone(out, t + 0.25, { type: "sine", f0: r(600, 700), f1: 1100, dur: 0.06, gain: 0.12 * vol });
        break;
      case "refill":
        // Chest refill: a bell chime, three rising notes that ring out.
        [1047, 1319, 1568].forEach((f, i) => {
          this.tone(out, t + i * 0.13, { type: "sine", f0: f, dur: 0.7, gain: 0.18 * vol });
          this.tone(out, t + i * 0.13, { type: "sine", f0: f * 2.76, dur: 0.25, gain: 0.04 * vol });
        });
        break;
    }
  }

  private tone(out: AudioNode, t: number, o: { type: OscillatorType; f0: number; f1?: number; dur: number; gain: number; lp?: number; wobble?: number }) {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = o.type;
    osc.frequency.setValueAtTime(o.f0, t);
    if (o.f1) osc.frequency.exponentialRampToValueAtTime(o.f1, t + o.dur);
    if (o.wobble) {
      const lfo = ctx.createOscillator();
      const depth = ctx.createGain();
      lfo.frequency.value = o.wobble;
      depth.gain.value = o.f0 * 0.12;
      lfo.connect(depth).connect(osc.frequency);
      lfo.start(t);
      lfo.stop(t + o.dur);
    }
    let node: AudioNode = osc;
    if (o.lp) {
      const f = ctx.createBiquadFilter();
      f.type = "lowpass";
      f.frequency.value = o.lp;
      node = node.connect(f);
    }
    node.connect(this.env(out, t, o.dur, o.gain, 0.005));
    osc.start(t);
    osc.stop(t + o.dur + 0.02);
  }

  private noise(out: AudioNode, t: number, o: { dur: number; gain: number; type: BiquadFilterType; f0: number; f1?: number; q?: number; attack?: number }) {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter();
    f.type = o.type;
    f.frequency.setValueAtTime(o.f0, t);
    if (o.f1) f.frequency.exponentialRampToValueAtTime(o.f1, t + o.dur);
    f.Q.value = o.q ?? 0.7;
    src.connect(f).connect(this.env(out, t, o.dur, o.gain, o.attack ?? 0.003));
    src.start(t, Math.random() * 0.5);
    src.stop(t + o.dur + 0.02);
  }

  /** Quick attack, exponential-ish decay. */
  private env(out: AudioNode, t: number, dur: number, gain: number, attack: number) {
    const g = this.ctx!.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    g.connect(out);
    return g;
  }
}

/** What your feet are on, for footstep sounds. */
export function surfaceOf(block: number): Surface {
  if (block === B.SAND) return "sand";
  if (block === B.PLANKS || block === B.LOG || block === B.BOOKSHELF) return "wood";
  if (block === B.STONE || block === B.COBBLE || block === B.STONEBRICK || block === B.BEDROCK || block === B.GOLD || block === B.GLASS) return "stone";
  if (block === B.ASPHALT || block === B.BRICK || block === B.CONCRETE) return "stone";
  return "grass";
}
